/**
 * The ONLY place query keys are constructed.
 *
 * Note `cardsPrefix` vs `cards`: the board holds ONE cache entry per filter variant, keyed
 * by a hash of the filter. A cross-list card move must patch every one of them, so
 * optimistic updates use `setQueriesData({ queryKey: cardsPrefix(boardId) })` — plural —
 * and not just the currently-visible filter. Otherwise switching filters after a drag
 * shows the card back in its old position.
 */
export const qk = {
  me: () => ['me'] as const,
  abilities: (workspaceId?: string) => ['abilities', workspaceId ?? null] as const,
  /**
   * EVERY workspace's ruleset, for dropping them all.
   *
   * `abilities()` with no argument is not that — it is the key for the workspace-less
   * ruleset, `['abilities', null]`, which does not prefix-match `['abilities', 'wsp_x']`.
   * Invalidating with it while signed into a workspace matches nothing and silently does
   * nothing, which is how a stale permission set survives a role change.
   *
   * Same reasoning as `cardsPrefix` above: when the intent is "all variants", the key has to
   * say so rather than relying on a default argument that happens to look like a prefix.
   */
  abilitiesPrefix: () => ['abilities'] as const,
  permissionsCatalog: () => ['permissions', 'catalog'] as const,

  /** The bell. Polled, so it is its own key rather than folded into anything else. */
  notifications: () => ['notifications'] as const,

  workspaces: () => ['workspaces'] as const,
  members: () => ['members'] as const,
  /** Who the signed-in person may hand work to, and who may sign it off. */
  delegation: () => ['members', 'delegation'] as const,
  teamTree: () => ['teams', 'tree'] as const,
  teamDescendants: (teamId: string) => ['teams', teamId, 'descendants'] as const,
  teamWeek: (teamId: string, params: string) => ['teams', teamId, 'week', params] as const,

  boards: (workspaceId: string) => ['boards', workspaceId] as const,
  board: (boardId: string) => ['board', boardId] as const,

  cardsPrefix: (boardId: string) => ['board', boardId, 'cards'] as const,
  cards: (boardId: string, filterHash: string) =>
    ['board', boardId, 'cards', filterHash] as const,
  /** Cross-board queries ("my work") are not scoped to a single board. */
  cardSearchPrefix: () => ['cards', 'search'] as const,
  cardSearch: (filterHash: string) => ['cards', 'search', filterHash] as const,

  card: (cardId: string) => ['card', cardId] as const,
  /** When an overdue item will now land, and how often that date has moved. */
  eta: (cardId: string) => ['eta', cardId] as const,
  etaHistory: (cardId: string) => ['eta', cardId, 'history'] as const,
  etaPrefix: () => ['eta'] as const,
  cardComments: (cardId: string) => ['card', cardId, 'comments'] as const,

  workload: (params: string) => ['workload', params] as const,
  /** Keyed by the serialized filter — every period/grouping combination is its own answer. */
  insights: (params: string) => ['insights', params] as const,
  personWorkload: (memberId: string, params: string) => ['workload', memberId, params] as const,

  /** `includeArchived` changes the result set, so it is part of the key. */
  clients: (includeArchived = false) => ['clients', includeArchived] as const,
  client: (clientId: string) => ['client', clientId] as const,

  /**
   * Client requests awaiting triage, keyed by the serialized status/client filter.
   *
   * `clientRequestsPrefix` exists for the same reason `cardsPrefix` does: accepting or
   * declining moves a row from one status tab to another, so it invalidates EVERY filter
   * variant. Patching only the visible one leaves the request in the Submitted tab until
   * the page is reloaded.
   */
  clientRequestsPrefix: () => ['requests'] as const,
  clientRequests: (params: string) => ['requests', params] as const,

  /** Finance tracker rows and the fixed approver directory. */
  finance: (workspaceId: string) => ['finance', workspaceId] as const,
  financeApprovers: (workspaceId: string) => ['finance', workspaceId, 'approvers'] as const,

  /**
   * Daily reports. One prefix so a submit or a settings edit can invalidate every view
   * (due list, history, status board) in one call.
   */
  reportsPrefix: () => ['reports'] as const,
  reportTemplates: () => ['reports', 'templates'] as const,
  reportServices: () => ['reports', 'services'] as const,
  reportGroups: () => ['reports', 'groups'] as const,
  reportPending: (all: boolean) => ['reports', 'pending', all] as const,
  reportDue: (date: string) => ['reports', 'due', date] as const,
  reportStatus: (date: string) => ['reports', 'status', date] as const,
  reportPreview: (params: string) => ['reports', 'preview', params] as const,
  reportSubmissions: (params: string) => ['reports', 'submissions', params] as const,

  /** Live Intercom tickets, scoped by the active workspace's permission context. */
  supportTickets: (workspaceId: string) => ['support-tickets', workspaceId] as const,
  /** The org's configured L3 tech teams with their assignable people (org-wide, not per workspace). */
  supportTeams: () => ['support-teams'] as const,
  /** The Support tab (one clock per issue). `supportDesk()` is the prefix every desk action invalidates. */
  supportDesk: () => ['support-desk'] as const,
  supportDeskCases: (view: string, filters: string) => ['support-desk', 'cases', view, filters] as const,
  supportDeskCase: (id: string) => ['support-desk', 'case', id] as const,
  supportDeskMeta: () => ['support-desk', 'meta'] as const,
  supportDeskDashboard: (from: string, to: string) => ['support-desk', 'dashboard', from, to] as const,
  supportDeskConversation: (sourceId: string) => ['support-desk', 'conversation', sourceId] as const,
  supportDeskActivity: (id: string) => ['support-desk', 'activity', id] as const,
  supportDeskRelated: (id: string) => ['support-desk', 'related', id] as const,
  approversFor: (ownerId: string) => ['approvers-for', ownerId] as const,

  /**
   * The client portal. Prefixed so a portal cache entry can never collide with the internal
   * one for the same id — they are different projections of the same row, and the external
   * one is the narrow one.
   */
  portalMe: () => ['portal', 'me'] as const,
  portalRequests: () => ['portal', 'requests'] as const,
  portalItems: () => ['portal', 'items'] as const,
  /** One entry per item: the detail endpoint returns the item AND its comments together. */
  portalItem: (itemId: string) => ['portal', 'item', itemId] as const,

  projects: () => ['projects'] as const,
  project: (projectId: string) => ['project', projectId] as const,
  /** Every Client Project's phase and health — the list filters and the sidebar. */
  lifecycles: () => ['lifecycles'] as const,
  /** One Client Project's dashboard. */
  lifecycle: (projectId: string) => ['lifecycles', projectId] as const,
  lifecycleRelated: (projectId: string) => ['lifecycles', projectId, 'related'] as const,
  lifecycleMigration: (choices = '') => ['lifecycles', 'migration', choices] as const,
  /** Selected-project timeline query, separated from the Work dashboard's board caches. */
  projectTimeline: (projectId: string) => ['project', projectId, 'timeline'] as const,
  projectTimelineSettings: (projectId: string) => ['project', projectId, 'timeline-settings'] as const,
  /** The subfolders in one project. There is no org-wide list; the ids only mean anything here. */
  workGroups: (projectId: string) => ['project', projectId, 'groups'] as const,
  /** A geography belongs to one client, so the list is keyed by the client it was asked for. */
  geographies: (clientId?: string) => ['geographies', clientId ?? null] as const,

  fields: (workspaceId: string) => ['fields', workspaceId] as const,
  labels: (workspaceId: string) => ['labels', workspaceId] as const,
  savedViews: (workspaceId: string) => ['views', workspaceId] as const,
  roles: () => ['roles'] as const,
  auditLog: (params: string) => ['audit', params] as const,

  /**
   * The CRM's GoHighLevel screens. `ghl()` is the prefix every GHL write invalidates: GHL is the
   * system of record, so after an edit everything read from it is re-read rather than patched.
   */
  ghl: () => ['ghl'] as const,
  ghlSnapshot: () => ['ghl', 'snapshot'] as const,
  ghlAccount: (key: string) => ['ghl', 'account', key] as const,
  ghlBilling: (key: string) => ['ghl', 'account', key, 'billing'] as const,
  ghlEmails: (ids: string) => ['ghl', 'emails', ids] as const,
  ghlContactWork: (contactId: string) => ['ghl', 'contact-work', contactId] as const,
  ghlContacts: (view: string, q: string) => ['ghl', 'contacts', view, q] as const,
  ghlContact: (id: string) => ['ghl', 'contact', id] as const,
  ghlBusinesses: (q: string) => ['ghl', 'businesses', q] as const,
  ghlInbox: (filter: string, channel: string, q: string) => ['ghl', 'inbox', filter, channel, q] as const,
  ghlConversation: (id: string) => ['ghl', 'conversation', id] as const,
  ghlTasks: (status: string, assignee: string) => ['ghl', 'tasks', status, assignee] as const,
  ghlMeetings: (from: string, to: string) => ['ghl', 'meetings', from, to] as const,
  ghlCalendars: () => ['ghl', 'calendars'] as const,
  ghlSlots: (calendarId: string, from: string, to: string) => ['ghl', 'slots', calendarId, from, to] as const,
  ghlFunnel: (from: string, to: string) => ['ghl', 'funnel', from, to] as const,
} as const
